import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import UpgradeBanner from '../../UpgradeBanner';
import PublicTopBar from '../TopBar/PublicTopBar';
import { Box, Drawer } from '@mui/material';
import SideNavbar from '../SideNavbar';
import Div100vh from 'react-div-100vh';
import './masterLayout.scss';
import TopBar from '../TopBar/TopBar';
import { useNewNavigation } from '../../../utils/useNewNavigation';

import NewTopBar from './TopBar';
import useScreenSize from '../../../utils/useScreenSize';
import { useIsSidebarCollapsed } from '../../../utils/useIsSidebarCollapsed';
import useRouter from '../../../utils/useRouter';
import { routes } from '../../../constants';
import { spaces } from '../../../assets/styles/themes/tokens';
import ProfileAvatar from '../../ProfileAvatar/ProfileAvatar';
import MobileTopBar from './TopBar/MobileTopBar';
import SearchBar from '../../SearchBar';
import { NewNavigationOnboardingProvider } from './NewNavigationOnboardingContext';
import { MasterLayoutContext } from '../../../state/MasterLayoutProvider';
import NewNavigationOnboardingDialog from '../../NewNavigationOnboardingDialog/NewNavigationOnboardingDialog';

interface MasterLayoutProps {
  children: ReactNode[] | ReactNode;
  isPublic?: boolean;
  sidebarCollapsed?: boolean;
  forceNewNavigation?: boolean;
  customTopBarContent?: ReactNode;
  additionalTopBarContent?: ReactNode
  hideAvatar?: boolean;
}

const MasterLayout: React.FC<
  MasterLayoutProps
> = ({ 
  children,
  isPublic,
  sidebarCollapsed = false,
  forceNewNavigation,
  customTopBarContent,
  additionalTopBarContent,
  hideAvatar = false
}) => {
  const { router: { push } } = useRouter();
  const { isSmallScreen, isLargeScreen  } = useScreenSize();
  const showNewNavigation = useNewNavigation();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const shouldCollapseSidebar = useIsSidebarCollapsed();
  const isSidebarCollapsed = sidebarCollapsed || shouldCollapseSidebar;

  const mainContentRef = useRef(null);
  const { setMainContentContainer } = useContext(MasterLayoutContext);

  useEffect(() => {
    setMainContentContainer(mainContentRef.current);
  },[setMainContentContainer]);

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleLogoClick = () => {
    push(`/${routes.dashboard.root}`);
  };

  const topBarContent = isSmallScreen ? <MobileTopBar /> : (
    <Box sx={{
      gap: spaces.md,
      display: 'flex',
      flexDirection: 'row',
      flex: '1 0 0',
    }}
    >
      { !isSmallScreen &&
        <Box sx={{
          flex: '1 0 0',
          alignSelf: 'center',
        }}>
          { customTopBarContent || <SearchBar /> }
        </Box>
      }
      {additionalTopBarContent}
      {!hideAvatar && isLargeScreen && <ProfileAvatar />}
    </Box>
  );

  const newLayout = (
    <NewNavigationOnboardingProvider>
      <Box 
        sx={{
          display: 'grid',
          width: '100%',
          height: '100vh',
          gridTemplateRows: 'auto 1fr',
          gridTemplateAreas: `
        "header"
        "main"
      `,
        }}
        data-testid='master-layout-new-navigation'
      >  
        <Box
          sx={{
            gridArea: 'main',
            overflow: 'hidden',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          {isLargeScreen && <SideNavbar isCollapsed={isSidebarCollapsed} />}
          {!isLargeScreen && (
            <Drawer 
              open={isDrawerOpen} 
              anchor='right' 
              onClose={handleCloseDrawer}
            >
              <SideNavbar isCollapsed={false} isDrawer onCloseDrawer={handleCloseDrawer} />
            </Drawer>
          )}
          <NewNavigationOnboardingDialog open={isDrawerOpen} />
          <Box sx={{
            height: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <NewTopBar
              onHomeClick={handleLogoClick}
              onBurgerMenuClick={handleOpenDrawer}
              content={topBarContent}
            /> 
            <Box
              ref={mainContentRef}
              component='main'
              sx={{
                height: '100%',
                flex: 1,
                overflow: 'auto',
              }}
            >
              {children}
            </Box>
          </Box>
        </Box>
      </Box>
    </NewNavigationOnboardingProvider>
  );

  const oldLayout = <Div100vh>
    <div className='master-layout__wrapper'>
      <header className='master-layout__header'>
        <UpgradeBanner />
        {isPublic ? <PublicTopBar /> : <TopBar />}
      </header>
      <main className='master-layout__main'>
        {children}
      </main>
    </div>
  </Div100vh>;

  if (forceNewNavigation || showNewNavigation) {
    return newLayout;
  } else {
    return oldLayout;
  }
};

export default MasterLayout;
